import React, { useState, useRef, useEffect } from "react";

const Magnifier = ({
  children,
  zoomScale = 2.5,
  lensSize = 150,
  zoomPanelClassName = "w-[400px] h-[400px]",
}) => {
  const [showZoom, setShowZoom] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [zoomStyle, setZoomStyle] = useState({});
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const logicalWidth = containerRef.current.offsetWidth;
    const logicalHeight = containerRef.current.offsetHeight;

    // Detect zoom factor (visual width / logical width)
    const zoomFactor = rect.width / logicalWidth;

    // e.clientX/Y are viewport relative
    // rect.left/top are viewport relative
    const visualX = e.clientX - rect.left;
    const visualY = e.clientY - rect.top;

    // Convert visual mouse position to logical position within the component
    let x = visualX / zoomFactor;
    let y = visualY / zoomFactor;

    // Center the lens on the mouse (lensSize is in logical pixels)
    let lensX = x - lensSize / 2;
    let lensY = y - lensSize / 2;

    // Constrain lens within logical bounds
    if (lensX < 0) lensX = 0;
    if (lensX > logicalWidth - lensSize) lensX = logicalWidth - lensSize;
    if (lensY < 0) lensY = 0;
    if (lensY > logicalHeight - lensSize) lensY = logicalHeight - lensSize;

    setLensPos({ x: lensX, y: lensY });

    // Calculate translation for the zoomed content (scaled children)
    // The children in the zoom panel are clones, but they will be scaled by zoomScale.
    // To show the area under the lens, we offset the top-left of the zoomed panel.
    const translateX = -(lensX * zoomScale);
    const translateY = -(lensY * zoomScale);

    setZoomStyle({
      transform: `translate(${translateX}px, ${translateY}px) scale(${zoomScale})`,
      transformOrigin: "top left",
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative cursor-crosshair overflow-visible"
      onMouseEnter={() => setShowZoom(true)}
      onMouseLeave={() => setShowZoom(false)}
      onMouseMove={handleMouseMove}
    >
      {/* Original Content */}
      <div className="relative">
        {children}

        {/* Lens Overlay */}
        {showZoom && (
          <div
            className="absolute border border-[#91b5ff] pointer-events-none z-50 transition-[opacity] duration-200"
            style={{
              width: `${lensSize}px`,
              height: `${lensSize}px`,
              left: `${lensPos.x}px`,
              top: `${lensPos.y}px`,
              backgroundColor: "rgba(145, 181, 255, 0.05)",
              backgroundImage:
                "radial-gradient(rgba(145, 181, 255, 0.5) 1px, transparent 1px)",
              backgroundSize: "4px 4px",
            }}
          />
        )}
      </div>

      {/* Zoom Panel */}
      {showZoom && (
        <div
          className={`absolute left-[calc(100%+20px)] top-0 z-[100] border border-gray-200 bg-white shadow-xl overflow-hidden pointer-events-none transition-opacity duration-300 ${zoomPanelClassName}`}
        >
          <div className="w-full h-full relative" style={zoomStyle}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default Magnifier;
